import addUtilitiesInput from './src/plugins/addUtilitiesInput';
import addVariantEm from './src/plugins/addVariantEm';
import matchVariantPreviousSibling from './src/plugins/matchVariantPreviousSibling';
import matchUtilitiesAnimationDuration from './src/plugins/matchUtilitiesAnimationDuration';
const plugin = require('tailwindcss/plugin');

/** @type {import('tailwindcss').Config} */
module.exports = {
  corePlugins: {
    preflight: true,
  },
  theme: {
    fontSize: {
      '3xs': '0.5rem', // 8px
      '2xs': '0.6875rem', // 11px
      xs: '0.75rem', //12px
      sm: '0.875rem', //14px
      base: '1rem', //16px
      lg: '1.125rem', //18px
      xl: '1.375rem', //22px
      '2xl': '1.75rem', //28px
      '3xl': '2rem', //32px
    },
    extend: {
      animation: {
        fade: 'fade .4s linear',
        'fade-in-slide-left': 'fadeInSlideLeft .4s linear',
        shake: 'shake .4s linear',
      },
      colors: {
        grey: {
          50: '#fafafa',
          100: '#f5f5f5',
          200: '#e5e5e5',
          250: '#dadada',
          300: '#d4d4d4',
          400: '#a3a3a3',
          DEFAULT: '#737373',
          500: '#737373',
          600: '#525252',
          700: '#404040',
          800: '#262626',
          900: '#171717',
          950: '#0a0a0a',
        },
        blue: {
          50: '#e6efff',
          100: '#bfd8ff',
          200: '#99beff',
          300: '#73a3ff',
          400: '#4d89ff',
          DEFAULT: '#2369F9',
          500: '#2369F9',
          600: '#1e5de0',
          700: '#194fbf',
          800: '#133f99',
          900: '#0d2f73',
        },
        green: {
          50: '#E6F9ED',
          100: '#CFF3DB',
          200: '#9EE8B6',
          300: '#6FDD92',
          400: '#4BD577',
          DEFAULT: '#31DC72',
          500: '#31DC72',
          600: '#2BBB66',
          700: '#239A58',
          800: '#1C7A4A',
          900: '#145A3A',
        },
        red: {
          50: '#fce8ed',
          100: '#f9d1db',
          200: '#f4a3b7',
          300: '#ee7692',
          400: '#e9486e',
          DEFAULT: '#e31a4a',
          500: '#e31a4a',
          600: '#b6153b',
          700: '#88102c',
          800: '#5b0a1e',
          900: '#2d050f',
          950: '#170307',
        },
        orange: {
          200: '#FBF4EB',
          300: '#F2DCBF',
          400: '#FF9900',
          DEFAULT: '#F29322',
          500: '#F29322',
          700: '#CF7D1D',
        },
        avatar: {
          DEFAULT: 'var(--avatar-bg)',
          text: 'var(--avatar-text)',
        },
        black: '#021F1D',
        current: 'currentColor',
        info: {
          200: 'var(--info-xlight)',
          300: 'var(--info-light)',
          DEFAULT: 'var(--info)',
          500: 'var(--info)',
        },
        inherit: 'inherit',
        menu: 'var(--menu-bg)',
        primary: {
          200: 'rgb(var(--primary-lighter-rgb) / <alpha-value>)',
          300: 'var(--primary-light)',
          400: 'rgb(var(--primary-400-rgb) / <alpha-value>)',
          DEFAULT: 'var(--primary)',
          500: 'var(--primary)',
          600: 'var(--primary-dark)',
          700: 'var(--primary-darker)',
          800: 'var(--primary-darkest)',
          900: 'rgb(var(--primary-900-rgb) / <alpha-value>)',
        },
        purple: {
          DEFAULT: '#8A3FFC',
        },
        secondary: {
          300: 'var(--secondary-light)',
          DEFAULT: 'var(--secondary)',
          500: 'var(--secondary)',
          600: 'var(--secondary-dark)',
        },
        success: 'rgb(var(--success-rgb) / <alpha-value>)',
        teal: {
          200: '#46E4DC',
          DEFAULT: '#0EA199',
        },
        transparent: 'transparent',
        'grey-accent': {
          100: '#fbfaf5',
          200: '#beb1a8',
        },
        white: '#ffffff',
        yellow: {
          DEFAULT: '#F4CA64',
        },
        // Het marktsegment particulier (VStatusTag, adviseursoverzicht) — een label dat los
        // staat van het actieve scope-thema en dus bewust niet meekleurt.
        particulier: {
          DEFAULT: '#f9b00f',
        },
        // Het actieve scope-thema; alleen gezet onder :root[data-scope='…'] (_scope.css).
        scope: {
          accent: 'var(--scope-accent, #f9b00f)',
        },
      },
      height: {
        unset: 'unset',
      },
      backgroundImage: {
        'env-local': "url('@assets/images/env-bg-local.svg')",
        'env-test': "url('@assets/images/env-bg-test.svg')",
        'env-acceptatie': "url('@assets/images/env-bg-acceptatie.svg')",
      },
      boxShadow: {
        sm: '0 3px 5px rgb(0 0 0 / .05)',
        top: '0 -0.9375rem 0.9375rem -1.125rem rgb(0 0 0 / 0.1)',
      },
      gap: {
        ch: '1ch',
      },
      margin: {
        ch: '1ch',
      },
      padding: {
        ch: '1ch',
      },
      keyframes: {
        fadeInSlideLeft: {
          '0%': { opacity: '0', translate: '2rem' },
          '100%': { opacity: '1' },
        },
        fade: {
          '0%': { opacity: '0' },
          '100%': { opacity: '1' },
        },
        shake: {
          '25%': { translate: '.375rem' },
          '50%': { translate: '-.25rem' },
          '75%': { translate: '.125rem' },
          '100%': { translate: '0' },
        },
      },
      maxWidth: {
        /**
         * 70ch valt binnen alle marges voor tekst met een formaat van 14-18px en maximaliseert de
         * hoeveelheid tekst die we kwijt kunnen door het plafond van 14px tekst te gebruiken.
         *
         * Zie ook: https://medium.com/@wblekhoa/talk-aboutthe-optimal-length-of-text-in-ux-ui-525e689f0b71
         */
        'readable-text': '70ch',
      },
      minHeight: {
        'form-row': 'var(--min-height-form-row)',
      },
      screens: {
        xs: '425px',
        '3xl': '1912px',
      },
      transitionDuration: {
        400: '.4s',
      },
      zIndex: {
        //Voor popups e.d. die altijd over de rest gerenderd moeten worden
        overlay: 250,
        //Voor widgets (kalenders e.d.) die over alle pagina-content (inclusief headers) gerenderd moeten worden maar onder overlay elementen zoals popups en modals
        widget: 220,
        //Headers die over de content heen gerenderd moeten worden
        header: 200,
        //Subheaders die over de content heen, maar onder primaire headers gerenderd moeten worden.
        subheader: 150,
        //dialog boxes die boven de overige content moeten vallen
        dialog: 100,
      },
    },
    fontFamily: {
      sans: ['Open Sans', 'sans-serif'],
      mono: ['Fira Mono', 'Courier New'],
    },
  },
  plugins: [
    require('@tailwindcss/container-queries'),
    addUtilitiesInput,
    addVariantEm,
    matchUtilitiesAnimationDuration,
    matchVariantPreviousSibling,
    plugin(({ addUtilities, addVariant, matchUtilities }) => {
      addUtilities({
        '.clip-path-checkmark': {
          clipPath: 'polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%)',
        },
        '.display-unset': {
          display: 'unset',
        },
        '.overflow-wrap-anywhere': {
          overflowWrap: 'anywhere',
        },
        '.scrollbar-light': {
          scrollbarColor: 'rgb(255 255 255 / 0.25) transparent',
        },
        '.scrollbar-thin': {
          scrollbarWidth: 'thin',
        },
      });

      addVariant('not-first', '&:not(:first-child)');
      addVariant('not-hover', '&:not(:hover)');
      addVariant('not-last', '&:not(:last-child)');

      matchUtilities({
        'clip-path': (value) => ({
          'clip-path': value,
        }),
      });
    }),
  ],
};
